// ============================================================================
// USE COMPARE RUNNER
//
// Manages two independent algorithm executions driven by a single shared
// playback cursor so both canvases always show the same logical step index.
//
// Design:
//  • Each slot (A / B) stores its own ExecutionStep[] produced by the existing
//    runLocalSimulation path (via useAlgorithmRunner internally).
//  • A single `currentStepIndex` is shared and clamped to
//    min(stepsA.length, stepsB.length) - 1 so neither side overflows.
//  • Play / pause / step / seek / speed all act on the shared cursor.
//  • Both algorithms are executed against the SAME inputData so results are
//    comparable.  If they have different input shapes the runner applies
//    the same multi-candidate fallback logic that the normal runner uses.
// ============================================================================

import { useState, useCallback, useRef, useEffect, useReducer } from 'react';
import type { ExecutionStep } from '../types/rust-engine-interfaces';
import type { AlgorithmMeta } from '../registry/algorithmRegistry';
import { buildInputData } from '../utils/inputDataBuilder';
import { runLocalSimulationExport } from './useAlgorithmRunner';

// ── Shared playback state ────────────────────────────────────────────────────

interface PlaybackState {
  currentStepIndex: number;
  isPlaying: boolean;
  speed: number;
}

type PlaybackAction =
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TICK'; maxIndex: number }
  | { type: 'STEP_FORWARD'; maxIndex: number }
  | { type: 'STEP_BACKWARD' }
  | { type: 'SEEK'; index: number; maxIndex: number }
  | { type: 'SET_SPEED'; speed: number }
  | { type: 'RESET' };

function clamp(v: number, max: number) {
  return Math.max(0, Math.min(max, v));
}

function playbackReducer(state: PlaybackState, action: PlaybackAction): PlaybackState {
  switch (action.type) {
    case 'PLAY':
      return { ...state, isPlaying: true };
    case 'PAUSE':
      return { ...state, isPlaying: false };
    case 'TICK': {
      const next = state.currentStepIndex + 1;
      if (next > action.maxIndex) return { ...state, isPlaying: false };
      return { ...state, currentStepIndex: next };
    }
    case 'STEP_FORWARD':
      return { ...state, currentStepIndex: clamp(state.currentStepIndex + 1, action.maxIndex), isPlaying: false };
    case 'STEP_BACKWARD':
      return { ...state, currentStepIndex: Math.max(0, state.currentStepIndex - 1), isPlaying: false };
    case 'SEEK':
      return { ...state, currentStepIndex: clamp(action.index, action.maxIndex), isPlaying: false };
    case 'SET_SPEED':
      return { ...state, speed: Math.max(0.25, Math.min(8, action.speed)) };
    case 'RESET':
      return { ...state, currentStepIndex: 0, isPlaying: false };
    default:
      return state;
  }
}

// ── Slot type ────────────────────────────────────────────────────────────────

export interface CompareSlot {
  algo: AlgorithmMeta | null;
  steps: ExecutionStep[];
  isLoading: boolean;
  error: string | null;
}

// ── Return type ──────────────────────────────────────────────────────────────

export interface UseCompareRunnerReturn {
  slotA: CompareSlot;
  slotB: CompareSlot;
  /** Index into both step arrays (clamped to the shorter one) */
  currentStepIndex: number;
  isPlaying: boolean;
  speed: number;
  totalSteps: number;
  canStepForward: boolean;
  canStepBackward: boolean;
  setSlotAlgo: (slot: 'A' | 'B', algo: AlgorithmMeta) => void;
  clearSlot: (slot: 'A' | 'B') => void;
  play: () => void;
  pause: () => void;
  stepForward: () => void;
  stepBackward: () => void;
  seekTo: (index: number) => void;
  setSpeed: (speed: number) => void;
  reset: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useCompareRunner(): UseCompareRunnerReturn {
  const [slotA, setSlotA] = useState<CompareSlot>({ algo: null, steps: [], isLoading: false, error: null });
  const [slotB, setSlotB] = useState<CompareSlot>({ algo: null, steps: [], isLoading: false, error: null });

  const [pb, dispatch] = useReducer(playbackReducer, {
    currentStepIndex: 0,
    isPlaying: false,
    speed: 1.0,
  });

  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);
  const executionIdRefA = useRef<number>(0);
  const executionIdRefB = useRef<number>(0);

  const totalSteps = Math.max(
    slotA.steps.length > 0 && slotB.steps.length > 0
      ? Math.min(slotA.steps.length, slotB.steps.length)
      : Math.max(slotA.steps.length, slotB.steps.length),
    0,
  );
  const maxIndex = Math.max(totalSteps - 1, 0);

  // ── Interval management ───────────────────────────────────────────────────

  const clearTick = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTickRef.current = 0;
  }, []);

  useEffect(() => {
    clearTick();
    if (!pb.isPlaying || totalSteps === 0) return;
    const delayMs = 800 / pb.speed;
    const tick = (ts: number) => {
      if (lastTickRef.current === 0) lastTickRef.current = ts;
      if (ts - lastTickRef.current >= delayMs) {
        lastTickRef.current = ts;
        dispatch({ type: 'TICK', maxIndex });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return clearTick;
  }, [pb.isPlaying, pb.speed, totalSteps, maxIndex, clearTick]);

  useEffect(() => {
    if (totalSteps === 0) {
      dispatch({ type: 'RESET' });
      return;
    }
    if (pb.currentStepIndex > maxIndex) {
      dispatch({ type: 'SEEK', index: maxIndex, maxIndex });
    }
  }, [pb.currentStepIndex, totalSteps, maxIndex]);

  useEffect(() => () => clearTick(), [clearTick]);

  // ── Slot execution ────────────────────────────────────────────────────────

  const executeSlot = useCallback(async (
    which: 'A' | 'B',
    algo: AlgorithmMeta,
    inputData: unknown,
  ) => {
    const setter = which === 'A' ? setSlotA : setSlotB;
    const execRef = which === 'A' ? executionIdRefA : executionIdRefB;
    execRef.current += 1;
    const currentRunId = execRef.current;
    setter(prev => ({ ...prev, algo, steps: [], isLoading: true, error: null }));
    dispatch({ type: 'RESET' });
    try {
      const steps = await runLocalSimulationExport(algo.id, inputData);
      if (currentRunId !== execRef.current) return;
      setter(prev => ({ ...prev, steps, isLoading: false }));
    } catch (e) {
      if (currentRunId !== execRef.current) return;
      setter(prev => ({ ...prev, isLoading: false, error: e instanceof Error ? e.message : 'Execution failed' }));
    }
  }, []);

  const setSlotAlgo = useCallback((slot: 'A' | 'B', algo: AlgorithmMeta) => {
    const inputData = buildInputData(algo);
    void executeSlot(slot, algo, inputData);
  }, [executeSlot]);

  const clearSlot = useCallback((slot: 'A' | 'B') => {
    const setter = slot === 'A' ? setSlotA : setSlotB;
    setter({ algo: null, steps: [], isLoading: false, error: null });
    dispatch({ type: 'RESET' });
  }, []);

  // ── Controls ──────────────────────────────────────────────────────────────

  const play          = useCallback(() => {
    if (totalSteps > 0) dispatch({ type: 'PLAY' });
  }, [totalSteps]);
  const pause         = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const stepForward   = useCallback(() => dispatch({ type: 'STEP_FORWARD', maxIndex }), [maxIndex]);
  const stepBackward  = useCallback(() => dispatch({ type: 'STEP_BACKWARD' }), []);
  const reset         = useCallback(() => dispatch({ type: 'RESET' }), []);
  const seekTo        = useCallback((index: number) => {
    clearTick();
    dispatch({ type: 'SEEK', index, maxIndex });
  }, [clearTick, maxIndex]);
  const setSpeed      = useCallback((speed: number) => dispatch({ type: 'SET_SPEED', speed }), []);

  const currentStepIndex = clamp(pb.currentStepIndex, maxIndex);

  return {
    slotA,
    slotB,
    currentStepIndex,
    isPlaying: pb.isPlaying,
    speed: pb.speed,
    totalSteps,
    canStepForward:  totalSteps > 0 && currentStepIndex < maxIndex,
    canStepBackward: currentStepIndex > 0,
    setSlotAlgo,
    clearSlot,
    play,
    pause,
    stepForward,
    stepBackward,
    seekTo,
    setSpeed,
    reset,
  };
}
